import { Button, ButtonIcon, UserAvatar } from "@platform/ui/components";
import { copy, heading } from "@platform/ui/styles";
import { useMutation, useQueryClient, useSuspenseQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router";
import { twMerge } from "tailwind-merge";
import { UTAH_PROGRAM_AVAILABLE_STEPS, UTAH_PROGRAM_ID } from "~/participant/constants/utah.constants";
import { pageTitle } from "~/participant/styles/utah.styles";
import { GlassCard } from "~/participant/views/utah-onboarding/components/GlassCard";
import { SplitLayoutPortal } from "~/participant/views/utah-onboarding/components/SplitLayout";
import api from "~/shared/api/core/api";
import { USERS_KEYS, USERS_QUERIES } from "~/shared/api/users/users.queries";
import { toast } from "~/shared/components/ds/Toast/Toast";

export function UtahOnboardingWelcomeView() {
  const queryClient = useQueryClient();
  const { data: user } = useSuspenseQuery(USERS_QUERIES.profile());
  const navigate = useNavigate();
  const utahWelcomeMutation = useMutation({
    mutationFn: async () => {
      await api.post(`/participants/${user.id}/onboarding`, {
        programId: UTAH_PROGRAM_ID,
        step: UTAH_PROGRAM_AVAILABLE_STEPS.welcome.id,
        part: 1,
        payload: null,
      });
    },
    onSuccess: () => {
      return queryClient.invalidateQueries({
        queryKey: USERS_KEYS.all,
      });
    },
  });

  function handleStart() {
    utahWelcomeMutation.mutate(undefined, {
      onSuccess: () => {
        navigate(UTAH_PROGRAM_AVAILABLE_STEPS["basic-info"].slug);
      },
      onError: (error) => {
        toast.api.error({ error, title: "Failed to complete welcome step" });
      },
    });
  }

  return (
    <>
      <SplitLayoutPortal>
        <div className="flex h-full w-full items-center justify-center">
          <GlassCard outerClassName="w-full max-w-[375px]" innerClassName="flex flex-col gap-9 p-9">
            <div className="flex flex-col gap-5">
              <h2
                className={heading({
                  variant: "20",
                  weight: "medium",
                })}
              >
                What we'll need
              </h2>
              <ol className="m-0 flex list-none flex-col gap-2 p-0">
                {WHAT_WE_CHECK.map((item, index) => {
                  return (
                    <li key={index} className="flex items-center gap-3">
                      <span className="border-ds-stroke-primary text-ds-text-secondary flex h-5 w-5 shrink-0 items-center justify-center rounded-full border text-xs">
                        {index + 1}
                      </span>
                      <span className="text-ds-text-secondary text-sm">{item}</span>
                    </li>
                  );
                })}
              </ol>
            </div>

            <Button
              size="lg"
              suffix={<ButtonIcon symbol="arrow-right-line" />}
              onClick={handleStart}
              disabled={utahWelcomeMutation.isPending}
              isLoading={utahWelcomeMutation.isPending}
            >
              Get Started
            </Button>
          </GlassCard>
        </div>
      </SplitLayoutPortal>
      <div className="mx-auto grid h-full grid-cols-1 place-items-center gap-16 px-5 md:px-10">
        <div className="flex max-w-prose flex-col gap-4">
          <h1 className={twMerge(pageTitle, "font-medium text-balance")}>
            Welcome to the Career Advancement Initiative for the Military Community in Utah
          </h1>
          <p
            className={copy({
              variant: "16",
              color: "secondary",
            })}
          >
            We are looking forward to getting to know you and to helping you find the next step in your career. Our team
            is passionate about supporting the success of our military community within the state of Utah. Hundreds of
            employers are also at the table, looking for you! Our team and platform will provide you with education,
            guidance, and community, every step of the way, in support of your goals.
          </p>
          <div className="flex gap-3">
            <UserAvatar headshot="/images/headshots/lauren-faul-headshot.jpeg" alt="Lauren Faul headshot" />
            <span className="flex flex-col">
              <span className="text-ds-text-secondary text-sm">Lauren Faul</span>
              <span className="text-ds-text-tertiary text-sm">Director of Advancement, Military Spouse</span>
            </span>
          </div>
        </div>

        <Button
          size="lg"
          suffix={<ButtonIcon symbol="arrow-right-line" />}
          className="w-full lg:hidden"
          disabled={utahWelcomeMutation.isPending}
          isLoading={utahWelcomeMutation.isPending}
          onClick={handleStart}
        >
          Get Started
        </Button>
      </div>
    </>
  );
}

const WHAT_WE_CHECK = [
  "Basic Information",
  "Self-Identification",
  "Skills and Industries",
  <span>
    Bonus <span className="text-ds-text-tertiary text-xs">(optional)</span>
  </span>,
];
